import React, { useState, useEffect, useRef } from 'react'
import { livingRoomLeft_Image } from '../../constants/imageConstants'
import { AiOutlineClose } from 'react-icons/ai'
import { contactPopupData } from '../../constants/data'
import { Input } from '../UsedInputs'
import { Controller, useForm } from "react-hook-form";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import { yupResolver } from '@hookform/resolvers/yup'
import { ContactPopupValidation } from '../Validations/ContactPopupValidation'
import { InlineError } from '../Notifications/Error'
import '../../styles/contactModalFormStyles.css'
import { useDispatch } from 'react-redux'
import { contactScreenAction } from '../../redux/actions/clientFormAction'
import Loader from '../Notifications/Loader'

const id = Math.floor(Math.random() * 1000000000);
const workorder_id = Math.floor(Math.random() * 1000000000);

// ─── Responsive hook ──────────────────────────────────────────────────────────
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
};

// ─── Trust Badges ─────────────────────────────────────────────────────────────
const BADGES = [
  { icon: '✦', title: 'Custom Designs',     sub: 'Tailored to your style and needs'  },
  { icon: '◎', title: 'End-to-End Support', sub: 'From concept to completion'         },
  { icon: '⚙', title: 'Trusted by every clients',  sub: 'Homes designed with care & passion' },
];

// ─── City icons ───────────────────────────────────────────────────────────────
const getCityIcon = (name) => {
  const icons = { Vizag: '⚓', Hyderabad: '🏙'};
  return icons[name] || '📍';
};

// ─── Main component ───────────────────────────────────────────────────────────
const ContactPopupModal = ({ setIsModalOpen }) => {
  const isMobile = useIsMobile();

  const [isSelectedProject,    setIsSelectedProject]    = useState('');
  const [isTypeOfProjectIndex, setIsTypeOfProjectIndex] = useState(-1);
  const [isSelectedPlace,      setIsSelectedPlace]      = useState('');
  const [isTypeOfPlaceIndex,   setIsTypeOfPlaceIndex]   = useState(-1);
  const [isSelectedArea,       setIsSelectedArea]       = useState('');
  const [isTypeOfAreaIndex,    setIsTypeOfAreaIndex]    = useState(-1);
  const [isLoading,            setLoading]              = useState(false);

  // ── Auto-scroll refs ────────────────────────────────────────────────────────
  const placeRef    = useRef(null);
  const areaRef     = useRef(null);
  const formRef     = useRef(null);
  const scrollPanel = useRef(null);

  const smoothScrollTo = (ref) => {
    setTimeout(() => {
      if (ref?.current && scrollPanel?.current) {
        const panelTop = scrollPanel.current.getBoundingClientRect().top;
        const elemTop  = ref.current.getBoundingClientRect().top;
        const offset   = elemTop - panelTop + scrollPanel.current.scrollTop - 12;
        scrollPanel.current.scrollTo({ top: offset, behavior: 'smooth' });
      }
    }, 100);
  };

  const dispatch = useDispatch();

  const onDismiss = () => {
    setIsModalOpen(false);
    document.body.style.overflow = 'unset';
  };

  const onHandleTypeOfProject = (type, index) => {
    setIsSelectedProject(type);
    setIsTypeOfProjectIndex(index);
    smoothScrollTo(placeRef);
  };
  const onHandleTypeOfPlace = (type, index) => {
    setIsSelectedPlace(type);
    setIsTypeOfPlaceIndex(index);
    smoothScrollTo(areaRef);
  };
  const onHandleTypeOfArea = (type, index) => {
    setIsSelectedArea(type);
    setIsTypeOfAreaIndex(index);
    smoothScrollTo(formRef);
  };

  const { register, handleSubmit, control, formState: { errors } } = useForm({
    resolver: yupResolver(ContactPopupValidation),
  });

  const onSubmit = async (data) => {
    const endData = { ...data, area: isSelectedArea, place_type: isSelectedPlace, typeOfProject: isSelectedProject, message: '' };
    setLoading(true);
    await dispatch(contactScreenAction(endData, setLoading, id, workorder_id));
    setTimeout(() => { onDismiss(); setLoading(false); }, 1000);
  };

  const onErrors = (errors) => console.log(errors);

  // ── MOBILE layout ──────────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <div
        data-aos="fade-down"
        style={{
          display: 'flex',
          flexDirection: 'column',
          width: '96%',
          maxWidth: '440px',
          maxHeight: '92vh',
          margin: 'auto',
          borderRadius: '16px',
          boxShadow: '0 25px 60px rgba(0,0,0,0.35)',
          position: 'relative',
          fontFamily: "'Georgia', serif",
          overflow: 'hidden',
        }}
      >
        {/* Close Button */}
        <button
          onClick={onDismiss}
          style={{
            position: 'absolute', top: '12px', right: '12px',
            width: '32px', height: '32px', borderRadius: '50%',
            background: 'rgba(255,255,255,0.97)', border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 30, boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
          }}
        >
          <AiOutlineClose style={{ width: '14px', height: '14px', color: '#1e4a3a' }} />
        </button>

        {/* ── Hero Image + Badge Card (fixed height, never scrolls) ── */}
        <div style={{ position: 'relative', flexShrink: 0, height: '240px' }}>
          <img
            alt=""
            src={livingRoomLeft_Image}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
          {/* Dark gradient overlay */}
          <div style={{
            position: 'absolute', inset: 0,
            background: 'linear-gradient(180deg, rgba(30,74,58,0.65) 0%, rgba(0,0,0,0.5) 100%)',
          }} />

          {/* Brand name — top left */}
          <div style={{ position: 'absolute', top: '14px', left: '14px', zIndex: 2 }}>
            <p style={{
              margin: 0, color: '#c9a96e', fontSize: '12px',
              fontFamily: 'sans-serif', fontWeight: '800',
              letterSpacing: '2.5px', textTransform: 'uppercase',
            }}>
              ZIVA INTERIORS
            </p>
            <p style={{ margin: '2px 0 0', color: 'rgba(255,255,255,0.8)', fontSize: '10px', fontFamily: 'sans-serif' }}>
              Transform · Design · Elevate
            </p>
          </div>

          {/* Badge card — bottom of image, full width */}
          <div style={{
            position: 'absolute', bottom: '12px', left: '12px', right: '12px', zIndex: 2,
            background: 'rgba(22,56,44,0.96)',
            borderRadius: '12px',
            padding: '12px 14px',
            display: 'flex', flexDirection: 'column', gap: '10px',
          }}>
            {BADGES.map(badge => (
              <div key={badge.title} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{
                  width: '30px', height: '30px', borderRadius: '50%',
                  background: 'rgba(255,255,255,0.12)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0, color: '#c9a96e', fontSize: '13px',
                }}>
                  {badge.icon}
                </div>
                <div>
                  <p style={{ margin: 0, color: '#ffffff', fontSize: '12px', fontWeight: '700', fontFamily: 'sans-serif' }}>
                    {badge.title}
                  </p>
                  <p style={{ margin: '2px 0 0', color: 'rgba(255,255,255,0.6)', fontSize: '10px', fontFamily: 'sans-serif' }}>
                    {badge.sub}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Scrollable Form Panel ── */}
        <div
          ref={scrollPanel}
          style={{
            flex: 1,
            overflowY: 'auto',
            background: '#f7f4ef',
            padding: '18px 16px 28px',
            scrollbarWidth: 'none',
          }}
        >
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: '16px' }}>
            <h1 style={{ fontSize: '22px', fontFamily: "'Georgia', serif", color: '#1e3a2f', lineHeight: 1.3, margin: 0 }}>
              Get <span style={{ color: '#c9a96e' }}>Free</span> Design<br />Consultation
            </h1>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginTop: '8px' }}>
              <div style={{ height: '1px', width: '36px', background: '#c9a96e' }} />
              <div style={{ width: '5px', height: '5px', background: '#c9a96e', borderRadius: '50%' }} />
              <div style={{ height: '1px', width: '36px', background: '#c9a96e' }} />
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit, onErrors)}>

            {/* Type of Project */}
            <div style={{ marginBottom: '14px' }}>
              <SectionLabel>Tell us about your project</SectionLabel>
              <div style={{ display: 'flex', gap: '8px' }}>
                {contactPopupData?.typeOfProject?.map((item, index) => (
                  <button
                    type="button"
                    key={item.name}
                    onClick={() => onHandleTypeOfProject(item.name, index)}
                    style={{ ...projectCardStyle(isSelectedProject === item.name), flex: 1, padding: '8px 10px' }}
                  >
                    <span style={{ fontSize: '16px' }}>
                      {item.name === 'Residential' ? '🏠' : '🏢'}
                    </span>
                    <div style={{ textAlign: 'left' }}>
                      <p style={{ margin: 0, fontSize: '11px', fontWeight: '700', fontFamily: 'sans-serif' }}>{item.name}</p>
                      <p style={{ margin: 0, fontSize: '9px', opacity: 0.75, fontFamily: 'sans-serif' }}>
                        {item.name === 'Residential' ? 'Make your home amazing' : 'Design for business spaces'}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
              {isTypeOfProjectIndex > -1 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                  <img data-aos="fade-right" src={contactPopupData?.typeOfProject[isTypeOfProjectIndex][`${isSelectedProject}_Image`]} style={{ width: '16px', objectFit: 'contain' }} alt="" />
                  <p style={{ margin: 0, fontSize: '11px', color: '#4a6741', fontFamily: 'sans-serif', fontStyle: 'italic' }}>
                    {contactPopupData?.typeOfProject[isTypeOfProjectIndex][isSelectedProject]}
                  </p>
                </div>
              )}
            </div>

            {/* Type of Place */}
            {isTypeOfProjectIndex > -1 && (
              <div ref={placeRef} style={{ marginBottom: '14px' }}>
                <SectionLabel>Tell us about your place</SectionLabel>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '7px' }}>
                  {contactPopupData?.typeOfProject[isTypeOfProjectIndex].aboutYourPlace?.map((abItem, abIndex) => (
                    <button
                      type="button"
                      key={abItem.name}
                      onClick={() => onHandleTypeOfPlace(abItem.name, abIndex)}
                      style={{ ...chipStyle(isSelectedPlace === abItem.name), padding: '6px 12px', fontSize: '11px' }}
                    >
                      {abItem.name}
                    </button>
                  ))}
                </div>
                {isTypeOfPlaceIndex > -1 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                    <img data-aos="fade-right" src={contactPopupData?.typeOfProject[isTypeOfProjectIndex]?.aboutYourPlace[isTypeOfPlaceIndex].image} style={{ width: '16px', objectFit: 'contain' }} alt="" />
                    <p style={{ margin: 0, fontSize: '11px', color: '#4a6741', fontFamily: 'sans-serif', fontStyle: 'italic' }}>
                      {contactPopupData?.typeOfProject[isTypeOfProjectIndex]?.aboutYourPlace[isTypeOfPlaceIndex]?.description}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Area */}
            {isTypeOfProjectIndex > -1 && isTypeOfPlaceIndex > -1 && (
              <div ref={areaRef} style={{ marginBottom: '14px' }}>
                <SectionLabel>{contactPopupData?.areaTitle}</SectionLabel>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '7px' }}>
                  {contactPopupData?.area?.map((areaItem, index) => (
                    <button
                      type="button"
                      key={areaItem.name}
                      onClick={() => onHandleTypeOfArea(areaItem.name, index)}
                      style={{ ...chipStyleWithIcon(isSelectedArea === areaItem.name), padding: '6px 12px', fontSize: '11px' }}
                    >
                      <span style={{ fontSize: '12px' }}>{getCityIcon(areaItem.name)}</span>
                      {areaItem.name}
                    </button>
                  ))}
                </div>
                {isTypeOfAreaIndex > -1 && (
                  <div style={{ marginTop: '8px' }}>
                    {contactPopupData?.area[isTypeOfAreaIndex]?.description === false ? (
                      <Input label="" placeholder="Please specify other area..." type="text" bg={true} name="other_area" register={register} style={'py-2'} />
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <img alt="" data-aos="fade-right" src={contactPopupData?.area[isTypeOfAreaIndex]?.image} style={{ width: '16px', objectFit: 'contain' }} />
                        <p style={{ margin: 0, fontSize: '11px', color: '#4a6741', fontFamily: 'sans-serif', fontStyle: 'italic' }}>
                          {contactPopupData?.area[isTypeOfAreaIndex]?.description}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Form Fields */}
            {isTypeOfAreaIndex > -1 && (
              <div ref={formRef}>
                <div style={{ marginBottom: '12px' }}>
                  <FieldLabel>What is your Name?</FieldLabel>
                  <div style={inputWrapperStyle(!!errors.name)}>
                    <span style={{ color: '#aaa', fontSize: '15px', paddingLeft: '12px' }}>👤</span>
                    <input {...register('name')} placeholder="Enter your full name" style={{ ...inputInnerStyle, fontSize: '14px', padding: '12px' }} />
                  </div>
                  {errors.name && <InlineError text={errors.name.message} />}
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <FieldLabel>What is your Email?</FieldLabel>
                  <div style={inputWrapperStyle(!!errors.email)}>
                    <span style={{ color: '#aaa', fontSize: '15px', paddingLeft: '12px' }}>✉</span>
                    <input {...register('email')} placeholder="Enter your email address" style={{ ...inputInnerStyle, fontSize: '14px', padding: '12px' }} />
                  </div>
                  {errors.email && <InlineError text={errors.email.message} />}
                </div>

                <div style={{ marginBottom: '18px' }}>
                  <FieldLabel>Mobile number, so we can fix your appointment</FieldLabel>
                  <div style={inputWrapperStyle(!!errors.phonenumber)}>
                    <Controller
                      name="phonenumber" control={control}
                      rules={{ validate: (value) => isValidPhoneNumber(value) }}
                      render={({ field: { onChange, value } }) => (
                        <PhoneInput value={value} onChange={onChange} defaultCountry="IN" id="phonenumber"
                          style={{ width: '100%', padding: '12px', border: 'none', background: 'transparent', outline: 'none', fontFamily: 'sans-serif', fontSize: '14px' }}
                        />
                      )}
                    />
                  </div>
                  {errors.phonenumber && <InlineError text={errors.phonenumber.message} />}
                </div>

                <button
                  type="submit" disabled={isLoading}
                  style={{
                    width: '100%', padding: '14px',
                    background: '#1e4a3a', color: '#fff',
                    border: 'none', borderRadius: '8px',
                    fontSize: '13px', fontWeight: '700',
                    letterSpacing: '1.5px', textTransform: 'uppercase',
                    cursor: isLoading ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                    fontFamily: 'sans-serif',
                  }}
                  onMouseOver={e => { if (!isLoading) e.currentTarget.style.background = '#16382c'; }}
                  onMouseOut ={e => { if (!isLoading) e.currentTarget.style.background = '#1e4a3a'; }}
                >
                  {isLoading ? <Loader size={22} /> : <><span style={{ fontSize: '15px' }}>📅</span> Book Free Consultation</>}
                </button>

                <p style={{ textAlign: 'center', marginTop: '10px', fontSize: '11px', color: '#999', fontFamily: 'sans-serif' }}>
                  🔒 Your details are safe with us. No spam, ever.
                </p>
              </div>
            )}
          </form>
        </div>
      </div>
    );
  }

  // ── DESKTOP layout ────────────────────────────────────────────────────────────
  return (
    <div
      data-aos="fade-down"
      style={{
        display: 'flex',
        flexDirection: 'row',
        width: '90%',
        maxWidth: '900px',
        maxHeight: '92vh',
        margin: 'auto',
        borderRadius: '16px',
        overflow: 'hidden',
        boxShadow: '0 25px 60px rgba(0,0,0,0.35)',
        position: 'relative',
        fontFamily: "'Georgia', serif",
      }}
    >
      {/* Close Button */}
      <button
        onClick={onDismiss}
        style={{
          position: 'absolute', top: '12px', right: '12px',
          width: '32px', height: '32px', borderRadius: '50%',
          background: 'rgba(255,255,255,0.97)', border: 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
        }}
      >
        <AiOutlineClose style={{ width: '14px', height: '14px', color: '#1e4a3a' }} />
      </button>

      {/* Left Panel */}
      <div style={{ width: '38%', position: 'relative', flexShrink: 0 }}>
        <img alt="" src={livingRoomLeft_Image} style={{ objectFit: 'cover', height: '100%', width: '100%', display: 'block' }} />
        <div style={{
          position: 'absolute', bottom: '24px', left: '16px', right: '16px',
          background: '#1e4a3a', borderRadius: '12px',
          padding: '16px 14px', display: 'flex', flexDirection: 'column', gap: '12px',
        }}>
          {BADGES.map(badge => (
            <div key={badge.title} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              <div style={{
                width: '32px', height: '32px', borderRadius: '50%',
                background: 'rgba(255,255,255,0.12)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0, color: '#c9a96e', fontSize: '14px',
              }}>{badge.icon}</div>
              <div>
                <p style={{ margin: 0, color: '#fff', fontSize: '12px', fontWeight: '700', fontFamily: 'sans-serif' }}>{badge.title}</p>
                <p style={{ margin: 0, color: 'rgba(255,255,255,0.65)', fontSize: '10px', fontFamily: 'sans-serif', marginTop: '2px' }}>{badge.sub}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Right Panel */}
      <div ref={scrollPanel} style={{ flex: 1, background: '#f7f4ef', overflowY: 'auto', padding: '32px 28px 24px', scrollbarWidth: 'none' }}>
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <h1 style={{ fontSize: '26px', fontFamily: "'Georgia', serif", color: '#1e3a2f', lineHeight: 1.3, margin: 0 }}>
            Get <span style={{ color: '#c9a96e' }}>Free</span> Design<br />Consultation
          </h1>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginTop: '8px' }}>
            <div style={{ height: '1px', width: '36px', background: '#c9a96e' }} />
            <div style={{ width: '5px', height: '5px', background: '#c9a96e', borderRadius: '50%' }} />
            <div style={{ height: '1px', width: '36px', background: '#c9a96e' }} />
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit, onErrors)}>

          {/* Type of Project */}
          <div style={{ marginBottom: '16px' }}>
            <SectionLabel>Tell us about your project</SectionLabel>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {contactPopupData?.typeOfProject?.map((item, index) => (
                <button
                  type="button" key={item.name}
                  onClick={() => onHandleTypeOfProject(item.name, index)}
                  style={{ ...projectCardStyle(isSelectedProject === item.name), flex: '1 1 calc(50% - 4px)', minWidth: 'calc(50% - 4px)', padding: '10px 16px' }}
                >
                  <span style={{ fontSize: '16px' }}>{item.name === 'Residential' ? '🏠' : '🏢'}</span>
                  <div style={{ textAlign: 'left' }}>
                    <p style={{ margin: 0, fontSize: '13px', fontWeight: '700', fontFamily: 'sans-serif' }}>{item.name}</p>
                    <p style={{ margin: 0, fontSize: '10px', opacity: 0.75, fontFamily: 'sans-serif' }}>
                      {item.name === 'Residential' ? 'Make your home amazing' : 'Design for business spaces'}
                    </p>
                  </div>
                </button>
              ))}
            </div>
            {isTypeOfProjectIndex > -1 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                <img data-aos="fade-right" src={contactPopupData?.typeOfProject[isTypeOfProjectIndex][`${isSelectedProject}_Image`]} style={{ width: '16px', objectFit: 'contain' }} alt="" />
                <p style={{ margin: 0, fontSize: '12px', color: '#4a6741', fontFamily: 'sans-serif', fontStyle: 'italic' }}>
                  {contactPopupData?.typeOfProject[isTypeOfProjectIndex][isSelectedProject]}
                </p>
              </div>
            )}
          </div>

          {/* Type of Place */}
          {isTypeOfProjectIndex > -1 && (
            <div ref={placeRef} style={{ marginBottom: '16px' }}>
              <SectionLabel>Tell us about your place</SectionLabel>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {contactPopupData?.typeOfProject[isTypeOfProjectIndex].aboutYourPlace?.map((abItem, abIndex) => (
                  <button type="button" key={abItem.name} onClick={() => onHandleTypeOfPlace(abItem.name, abIndex)}
                    style={{ ...chipStyle(isSelectedPlace === abItem.name), padding: '8px 16px', fontSize: '12px' }}>
                    {abItem.name}
                  </button>
                ))}
              </div>
              {isTypeOfPlaceIndex > -1 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                  <img data-aos="fade-right" src={contactPopupData?.typeOfProject[isTypeOfProjectIndex]?.aboutYourPlace[isTypeOfPlaceIndex].image} style={{ width: '16px', objectFit: 'contain' }} alt="" />
                  <p style={{ margin: 0, fontSize: '12px', color: '#4a6741', fontFamily: 'sans-serif', fontStyle: 'italic' }}>
                    {contactPopupData?.typeOfProject[isTypeOfProjectIndex]?.aboutYourPlace[isTypeOfPlaceIndex]?.description}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Area */}
          {isTypeOfProjectIndex > -1 && isTypeOfPlaceIndex > -1 && (
            <div ref={areaRef} style={{ marginBottom: '16px' }}>
              <SectionLabel>{contactPopupData?.areaTitle}</SectionLabel>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {contactPopupData?.area?.map((areaItem, index) => (
                  <button type="button" key={areaItem.name} onClick={() => onHandleTypeOfArea(areaItem.name, index)}
                    style={{ ...chipStyleWithIcon(isSelectedArea === areaItem.name), padding: '8px 16px', fontSize: '12px' }}>
                    <span style={{ fontSize: '13px' }}>{getCityIcon(areaItem.name)}</span>
                    {areaItem.name}
                  </button>
                ))}
              </div>
              {isTypeOfAreaIndex > -1 && (
                <div style={{ marginTop: '8px' }}>
                  {contactPopupData?.area[isTypeOfAreaIndex]?.description === false ? (
                    <Input label="" placeholder="Please specify other area..." type="text" bg={true} name="other_area" register={register} style={'py-2'} />
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <img alt="" data-aos="fade-right" src={contactPopupData?.area[isTypeOfAreaIndex]?.image} style={{ width: '16px', objectFit: 'contain' }} />
                      <p style={{ margin: 0, fontSize: '12px', color: '#4a6741', fontFamily: 'sans-serif', fontStyle: 'italic' }}>
                        {contactPopupData?.area[isTypeOfAreaIndex]?.description}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Form Fields */}
          {isTypeOfAreaIndex > -1 && (
            <div ref={formRef}>
              <div style={{ marginBottom: '12px' }}>
                <FieldLabel>What is your Name?</FieldLabel>
                <div style={inputWrapperStyle(!!errors.name)}>
                  <span style={{ color: '#aaa', fontSize: '15px', paddingLeft: '12px' }}>👤</span>
                  <input {...register('name')} placeholder="Enter your full name" style={{ ...inputInnerStyle, fontSize: '13px', padding: '11px 12px' }} />
                </div>
                {errors.name && <InlineError text={errors.name.message} />}
              </div>

              <div style={{ marginBottom: '12px' }}>
                <FieldLabel>What is your Email?</FieldLabel>
                <div style={inputWrapperStyle(!!errors.email)}>
                  <span style={{ color: '#aaa', fontSize: '15px', paddingLeft: '12px' }}>✉</span>
                  <input {...register('email')} placeholder="Enter your email address" style={{ ...inputInnerStyle, fontSize: '13px', padding: '11px 12px' }} />
                </div>
                {errors.email && <InlineError text={errors.email.message} />}
              </div>

              <div style={{ marginBottom: '18px' }}>
                <FieldLabel>Mobile number, so we can fix your appointment</FieldLabel>
                <div style={inputWrapperStyle(!!errors.phonenumber)}>
                  <Controller
                    name="phonenumber" control={control}
                    rules={{ validate: (value) => isValidPhoneNumber(value) }}
                    render={({ field: { onChange, value } }) => (
                      <PhoneInput value={value} onChange={onChange} defaultCountry="IN" id="phonenumber"
                        style={{ width: '100%', padding: '10px 12px', border: 'none', background: 'transparent', outline: 'none', fontFamily: 'sans-serif', fontSize: '13px' }}
                      />
                    )}
                  />
                </div>
                {errors.phonenumber && <InlineError text={errors.phonenumber.message} />}
              </div>

              <button
                type="submit" disabled={isLoading}
                style={{
                  width: '100%', padding: '14px',
                  background: '#1e4a3a', color: '#fff',
                  border: 'none', borderRadius: '8px',
                  fontSize: '13px', fontWeight: '700',
                  letterSpacing: '1.5px', textTransform: 'uppercase',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                  fontFamily: 'sans-serif', transition: 'background 0.2s',
                }}
                onMouseOver={e => { if (!isLoading) e.currentTarget.style.background = '#16382c'; }}
                onMouseOut ={e => { if (!isLoading) e.currentTarget.style.background = '#1e4a3a'; }}
              >
                {isLoading ? <Loader size={22} /> : <><span style={{ fontSize: '15px' }}>📅</span> Book Free Consultation</>}
              </button>

              <p style={{ textAlign: 'center', marginTop: '10px', fontSize: '11px', color: '#999', fontFamily: 'sans-serif' }}>
                🔒 Your details are safe with us. No spam, ever.
              </p>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

// ─── Reusable sub-components ──────────────────────────────────────────────────

const SectionLabel = ({ children }) => (
  <p style={{ margin: '0 0 10px 0', fontSize: '13px', fontWeight: '700', color: '#1e3a2f', fontFamily: 'sans-serif', letterSpacing: '0.2px' }}>
    {children}
  </p>
);

const FieldLabel = ({ children }) => (
  <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#1e3a2f', fontFamily: 'sans-serif' }}>
    {children}
  </label>
);

// ─── Style helpers ────────────────────────────────────────────────────────────

const projectCardStyle = (selected) => ({
  display: 'flex', alignItems: 'center', gap: '10px',
  borderRadius: '8px',
  border: selected ? '2px solid #1e4a3a' : '2px solid #ddd',
  background: selected ? '#1e4a3a' : '#ffffff',
  color: selected ? '#ffffff' : '#333',
  cursor: 'pointer', transition: 'all 0.2s',
});

const chipStyle = (selected) => ({
  borderRadius: '6px',
  border: selected ? '2px solid #1e4a3a' : '1.5px solid #ccc',
  background: selected ? '#1e4a3a' : '#ffffff',
  color: selected ? '#ffffff' : '#444',
  cursor: 'pointer',
  fontWeight: selected ? '700' : '500',
  fontFamily: 'sans-serif',
  letterSpacing: '0.3px',
  transition: 'all 0.2s',
});

const chipStyleWithIcon = (selected) => ({
  ...chipStyle(selected),
  display: 'flex', alignItems: 'center', gap: '6px',
});

const inputWrapperStyle = (hasError) => ({
  display: 'flex', alignItems: 'center',
  background: '#ffffff',
  border: hasError ? '1.5px solid #e55' : '1.5px solid #ddd',
  borderRadius: '8px', overflow: 'hidden',
});

const inputInnerStyle = {
  flex: 1, border: 'none', outline: 'none',
  fontFamily: 'sans-serif', color: '#333', background: 'transparent',
};

export default ContactPopupModal;